import { useEffect, useState } from 'react';
import { folderTemplateFor } from '@erp-attach/shared';
import type { EntityType } from '@erp-attach/shared';
import type { DocumentSummary, FolderNode } from '../api/types';
import { isInternalDrag } from '../lib/documentDrag';
import { dropVerdict, findFolderIn } from '../lib/fileActions';
import { Icon } from './ui/Icon';
import { Chip } from './ui/Chip';

/**
 * Drops folders the caller cannot read (recursively) and orders template folders by the entity's
 * folder template (customer: Credit, Compliance, Legal, Other, Jobs, Archive; supplier: Financial,
 * Insurance, Contracts, Archive, Other) at whatever depth they sit (under the entity root on the
 * API). Anything else, such as per-site folders, is sorted by name.
 */
export function visibleFolders(folders: readonly FolderNode[], entityType: EntityType): FolderNode[] {
  const order = new Map(folderTemplateFor(entityType).map((n, i) => [n.name, i] as const));
  const rank = (f: FolderNode) => order.get(f.name) ?? Number.MAX_SAFE_INTEGER;
  const prune = (list: readonly FolderNode[]): FolderNode[] =>
    list
      .filter((f) => f.actions.includes('read'))
      .map((f) => ({ ...f, children: prune(f.children ?? []) }))
      .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  return prune(folders);
}

export interface FolderTreeProps {
  entityType: EntityType;
  folders: readonly FolderNode[];
  selectedId: string | null;
  onSelect: (folder: FolderNode) => void;
  /** The file being dragged from the files pane, if any: folders become drop targets. */
  dragDoc?: DocumentSummary | null;
  onDropDoc?: (folder: FolderNode) => void;
}

interface RowProps {
  folder: FolderNode;
  depth: number;
  selectedId: string | null;
  onSelect: (f: FolderNode) => void;
  dragDoc?: DocumentSummary | null;
  /** The folder the dragged file sits in (moves never leave its record). */
  dragSource?: FolderNode | null;
  onDropDoc?: (folder: FolderNode) => void;
}

function FolderRow({ folder, depth, selectedId, onSelect, dragDoc, dragSource = null, onDropDoc }: RowProps) {
  const hasChildren = folder.children.length > 0;
  const [expanded, setExpanded] = useState(folder.name !== 'Archive');
  const [hover, setHover] = useState(false);
  const selected = folder.id === selectedId;
  const archived = folder.status === 'archived';
  const verdict = dragDoc ? dropVerdict(folder, dragDoc, dragSource) : null;

  // Hovering a collapsed folder while dragging opens it, like a desktop file manager.
  useEffect(() => {
    if (!hover || !hasChildren || expanded) return;
    const t = setTimeout(() => setExpanded(true), 700);
    return () => clearTimeout(t);
  }, [hover, hasChildren, expanded]);
  useEffect(() => {
    if (!dragDoc) setHover(false);
  }, [dragDoc]);

  return (
    <li>
      <div className="flex items-center" style={{ paddingLeft: `${depth * 0.9}rem` }}>
        {hasChildren ? (
          <button
            type="button"
            className="gsl-btn gsl-btn-ghost gsl-btn-icon mr-0.5 !p-1"
            aria-label={expanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
          >
            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
          </button>
        ) : (
          <span className="mr-0.5 inline-block w-[26px]" aria-hidden="true" />
        )}
        <button
          type="button"
          className="gsl-tree-item"
          aria-current={selected ? 'true' : undefined}
          onClick={() => onSelect(folder)}
          data-folder-id={folder.id}
          data-drop={verdict ? (verdict.ok ? (hover ? 'over' : 'ok') : 'no') : undefined}
          title={verdict && !verdict.ok ? verdict.reason : undefined}
          onDragEnter={() => {
            if (dragDoc) setHover(true);
          }}
          onDragLeave={() => setHover(false)}
          onDragOver={(e) => {
            if (!dragDoc) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = verdict?.ok ? 'move' : 'none';
          }}
          onDrop={(e) => {
            setHover(false);
            if (!dragDoc || !verdict?.ok || !isInternalDrag(Array.from(e.dataTransfer?.types ?? []))) return;
            e.preventDefault();
            onDropDoc?.(folder);
          }}
        >
          <Icon name={selected || expanded ? 'folder-open' : 'folder'} size={16} className="shrink-0 text-muted-foreground" />
          <span className="truncate">{folder.name}</span>
          {typeof folder.documentCount === 'number' ? <span className="gsl-num ml-auto text-xs text-muted-foreground">{folder.documentCount}</span> : null}
          {archived && depth > 0 ? <Chip variant="neutral">archived</Chip> : null}
        </button>
      </div>
      {hasChildren && expanded ? (
        <ul className="mt-0.5 space-y-0.5">
          {folder.children.map((child) => (
            <FolderRow key={child.id} folder={child} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} dragDoc={dragDoc} dragSource={dragSource} onDropDoc={onDropDoc} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function FolderTree({ entityType, folders, selectedId, onSelect, dragDoc, onDropDoc }: FolderTreeProps) {
  const visible = visibleFolders(folders, entityType);
  const dragSource = dragDoc ? findFolderIn(folders, dragDoc.folderId) : null;
  if (visible.length === 0) {
    return <p className="px-2 py-3 text-sm text-muted-foreground">No folders you can read on this record.</p>;
  }
  return (
    <nav aria-label="Folders">
      <ul className="space-y-0.5">
        {visible.map((f) => (
          <FolderRow key={f.id} folder={f} depth={0} selectedId={selectedId} onSelect={onSelect} dragDoc={dragDoc} dragSource={dragSource} onDropDoc={onDropDoc} />
        ))}
      </ul>
    </nav>
  );
}
